/**
 * ParticipantWhatIfCards — Renders participant "Mitä jos?" / "Entä jos?"
 * comments from the audio transcript as a card grid, below the curated
 * AI question slots in ReportWhatIf.
 *
 * Filters out segments whose speaker_label is "Fasilitaattori" so only
 * participants' own questions surface as cards. If no participant
 * segments exist (e.g. older workshops without audio import), renders
 * nothing.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Quote } from 'lucide-react';
import { SITRA } from './reportStyles';
import EditableText from './EditableText';
import TranslatedText from './TranslatedText';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const ParticipantBlock = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid ${SITRA.border};
`;

const BlockTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${SITRA.blue};
  margin: 0 0 8px;
`;

const BlockHint = styled.div`
  font-size: 0.85rem;
  color: ${SITRA.textMuted};
  margin-bottom: 20px;
  font-style: italic;
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
`;

const Card = styled.article`
  background: ${SITRA.white};
  border: 1px solid ${SITRA.border};
  border-left: 4px solid ${SITRA.blue};
  padding: 18px 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 280px;
  min-width: 0;
  max-width: 100%;
`;

const QuoteIcon = styled.div`
  color: ${SITRA.blue};
  opacity: 0.55;
`;

const Body = styled.div`
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${SITRA.black};
  white-space: pre-line;
`;

const Footer = styled.footer`
  font-size: 0.78rem;
  color: ${SITRA.textMuted};
  display: flex;
  gap: 10px;
  align-items: center;
`;

const Speaker = styled.span`
  font-weight: 600;
  color: ${SITRA.textBody};
`;

function formatTime(secs) {
  if (secs == null) return '';
  const s = Math.round(Number(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

// Skip single-word utterances ("Kyllä.", "Yes.") that pass the speaker
// filter but make terrible cards. 5-word floor still lets short questions
// like "Mitä jos veroprosentti olisi 50%?" through.
const MIN_WORDS = 5;
function hasSubstance(text) {
  if (!text) return false;
  const words = text.trim().split(/\s+/).filter(w => /\p{L}/u.test(w));
  return words.length >= MIN_WORDS;
}

const ShowAllButton = styled.button`
  margin: 12px 0 0;
  padding: 8px 14px;
  border: 1px solid ${SITRA.border || '#d7d7d7'};
  border-radius: 999px;
  background: transparent;
  color: ${SITRA.primary || '#1a1a1a'};
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.04); }
  &:focus-visible { outline: 2px solid ${SITRA.primary || '#1a1a1a'}; outline-offset: 2px; }
`;

export default function ParticipantWhatIfCards({ segments = [] }) {
  const { t } = useTranslation();
  const participantSegments = segments.filter(s => {
    const label = (s.speaker_label || '').toLowerCase();
    if (!label || label.startsWith('fasilitaattori')) return false;
    return hasSubstance(s.text);
  });

  // A full Entä jos round is a dozen long cards; the first two carry the
  // section, the rest are there for whoever wants them.
  const INITIAL = 2;
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? participantSegments : participantSegments.slice(0, INITIAL);
  const hidden = participantSegments.length - visible.length;

  if (participantSegments.length === 0) return null;

  return (
    <ParticipantBlock>
      <BlockTitle>{t('report.participantWhatIf.title')}</BlockTitle>
      <BlockHint>
        {t('report.participantWhatIf.hint')}
      </BlockHint>
      <Grid>
        {visible.map(s => (
          <Card key={s.id || `${s.speaker_label}-${s.start_time}`}>
            <QuoteIcon><Quote size={18} /></QuoteIcon>
            <Body>
              {/* Editable per-segment text. Field key uses the
                  workshop_transcripts row id so an edit overrides only
                  that specific quote; falls back to the raw transcript
                  text when no edit exists. */}
              {s.id
                ? <TranslatedText
                    fieldKey={`transcript.${s.id}.text`}
                    fallback={s.text}
                    label={`${s.speaker_label || 'Osallistuja'} — kommentti`}
                  />
                : s.text}
            </Body>
            <Footer>
              <Speaker>{s.speaker_label}</Speaker>
              {s.start_time != null && <span>· {formatTime(s.start_time)}</span>}
            </Footer>
          </Card>
        ))}
      </Grid>
      {hidden > 0 && (
        <ShowAllButton type="button" onClick={() => setShowAll(true)}>
          {t('report.common.showAll', { count: participantSegments.length })}
        </ShowAllButton>
      )}
    </ParticipantBlock>
  );
}
